import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./relatorios.css";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../services/firebaseConnection";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const TIPOS_RELATORIO = [
  { id: "colaboradores_geral", label: "Dados dos colaboradores" },
  { id: "colaborador", label: "Relatório completo por colaborador" },
  { id: "treinamento", label: "Colaboradores por treinamento" },
  { id: "vencidos", label: "Treinamentos vencidos" },
  { id: "proximos", label: "Próximos do vencimento" },
  { id: "matrizes", label: "Relatório de matrizes" },
  { id: "auditoria", label: "Relatório de modificações" },
  { id: "lancamentos", label: "Relatório de lançamentos" },
];

const OPCOES_COLABORADORES = [
  { chave: "nome", label: "Nome" },
  { chave: "cpf", label: "CPF" },
  { chave: "obra", label: "Obra" },
  { chave: "funcao", label: "Função" },
  { chave: "quantidadeLancados", label: "Treinamentos lançados" },
  { chave: "quantidadePendentes", label: "Treinamentos pendentes" },
  { chave: "situacao", label: "Situação" },
];

const OPCOES_LANCAMENTOS = [
  { chave: "semanal", label: "Semanal" },
  { chave: "mensal", label: "Mensal" },
  { chave: "anual", label: "Anual" },
];

const OPCOES_ACAO_AUDITORIA = [
  { chave: "TODAS", label: "Todas as ações" },
  { chave: "CADASTRO", label: "Cadastro" },
  { chave: "EDICAO", label: "Edição / Atualização" },
  { chave: "ATIVACAO", label: "Ativação / Reativação" },
  { chave: "DESATIVACAO", label: "Desativação" },
  { chave: "EXCLUSAO", label: "Exclusão" },
];

const OPCOES_ENTIDADE_AUDITORIA = [
  { chave: "TODAS", label: "Todas as entidades" },
  { chave: "COLABORADOR", label: "Colaborador" },
  { chave: "TREINAMENTO", label: "Treinamento" },
  { chave: "MATRIZ", label: "Matriz" },
  { chave: "TREINAMENTO_MATRIZ", label: "Treinamento da matriz" },
];

const CACHE_TTL_MS = 1000 * 60 * 10;
const PREFIXO_CACHE = "@relatorios_treinamentos_";

function lerCache(chave) {
  try {
    const bruto = sessionStorage.getItem(`${PREFIXO_CACHE}${chave}`);
    if (!bruto) return null;

    const cache = JSON.parse(bruto);
    const expirou = Date.now() - cache.criadoEm > CACHE_TTL_MS;

    if (expirou) {
      sessionStorage.removeItem(`${PREFIXO_CACHE}${chave}`);
      return null;
    }

    return cache.valor;
  } catch (error) {
    console.error("Erro ao ler cache:", error);
    return null;
  }
}

function salvarCache(chave, valor) {
  try {
    sessionStorage.setItem(
      `${PREFIXO_CACHE}${chave}`,
      JSON.stringify({ criadoEm: Date.now(), valor }),
    );
  } catch (error) {
    console.error("Erro ao salvar cache:", error);
  }
}

function normalizarTexto(valor = "") {
  return valor
    .toString()
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['’`´"]/g, "")
    .replace(/[-–—]/g, " ")
    .replace(/[()/:.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function textoMaiusculo(valor = "") {
  return valor.toUpperCase();
}

function formatarDataBR(data) {
  if (!(data instanceof Date) || Number.isNaN(data.getTime())) return "";
  const dia = String(data.getDate()).padStart(2, "0");
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const ano = data.getFullYear();
  return `${dia}/${mes}/${ano}`;
}

function converterData(valor) {
  if (!valor) return null;

  if (valor?.toDate) {
    const dataFirestore = valor.toDate();
    return Number.isNaN(dataFirestore.getTime()) ? null : dataFirestore;
  }

  if (valor?.seconds) {
    const dataFirestore = new Date(valor.seconds * 1000);
    return Number.isNaN(dataFirestore.getTime()) ? null : dataFirestore;
  }

  if (valor instanceof Date) {
    return Number.isNaN(valor.getTime()) ? null : valor;
  }

  if (typeof valor === "string") {
    const texto = valor.trim();
    if (!texto) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
      const [ano, mes, dia] = texto.split("-").map(Number);
      return new Date(ano, mes - 1, dia);
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) {
      const [dia, mes, ano] = texto.split("/").map(Number);
      return new Date(ano, mes - 1, dia);
    }

    const iso = new Date(texto);
    if (!Number.isNaN(iso.getTime())) return iso;
  }

  return null;
}

function adicionarMeses(data, meses) {
  if (!(data instanceof Date) || Number.isNaN(data.getTime())) return null;

  const quantidade = Number(meses);
  if (Number.isNaN(quantidade)) return null;

  const novaData = new Date(data);
  const diaOriginal = novaData.getDate();

  novaData.setMonth(novaData.getMonth() + quantidade);

  if (novaData.getDate() !== diaOriginal) {
    novaData.setDate(0);
  }

  return novaData;
}

function diferencaEmDias(dataFutura, dataBase = new Date()) {
  if (!(dataFutura instanceof Date) || Number.isNaN(dataFutura.getTime())) {
    return null;
  }

  const inicio = new Date(
    dataBase.getFullYear(),
    dataBase.getMonth(),
    dataBase.getDate(),
  );

  const fim = new Date(
    dataFutura.getFullYear(),
    dataFutura.getMonth(),
    dataFutura.getDate(),
  );

  const diff = fim.getTime() - inicio.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function calcularSituacao(
  vencimento,
  realizado,
  limiteDias = 30,
  possuiVencimento = true,
) {
  if (!realizado) return "Pendente";
  if (!possuiVencimento) return "Sem validade";
  if (!vencimento) return "Sem validade";

  const dias = diferencaEmDias(vencimento);

  if (dias === null) return "Sem validade";
  if (dias < 0) return "Vencido";
  if (dias <= Number(limiteDias)) return "Próximo do vencimento";
  return "Em dia";
}

function pegarDataLancamento(treino) {
  return (
    converterData(treino?.dataCadastro) ||
    converterData(treino?.dataLancamento) ||
    converterData(treino?.cadastradoEm) ||
    converterData(treino?.criadoEm) ||
    converterData(treino?.createdAt)
  );
}

function treinamentoFoiSubstituido(treino) {
  return (
    treino?.substituido === true ||
    normalizarTexto(treino?.statusLancamento) === "SUBSTITUIDO" ||
    normalizarTexto(treino?.situacaoLancamento) === "SUBSTITUIDO"
  );
}

function ehLancamentoOriginal(treino) {
  return (
    !treinamentoFoiSubstituido(treino) && Boolean(pegarDataLancamento(treino))
  );
}

function filtrarTreinamentosValidos(lista = []) {
  return lista.filter((treino) => !treinamentoFoiSubstituido(treino));
}

function filtrarLancamentosOriginais(lista = []) {
  return lista.filter((treino) => ehLancamentoOriginal(treino));
}

function formatarPeriodoLancamento(data, periodo) {
  if (!data) return "SEM DATA";

  if (periodo === "anual") {
    return String(data.getFullYear());
  }

  if (periodo === "mensal") {
    return `${String(data.getMonth() + 1).padStart(2, "0")}/${data.getFullYear()}`;
  }

  const primeiroDiaAno = new Date(data.getFullYear(), 0, 1);
  const dias = Math.floor((data - primeiroDiaAno) / (24 * 60 * 60 * 1000));
  const semana = Math.ceil((dias + primeiroDiaAno.getDay() + 1) / 7);

  return `SEMANA ${String(semana).padStart(2, "0")}/${data.getFullYear()}`;
}

function estaNoPeriodo(data, inicio, fim) {
  if (!data) return false;

  const dataBase = new Date(
    data.getFullYear(),
    data.getMonth(),
    data.getDate(),
  );

  if (inicio) {
    const dataInicio = converterData(inicio);
    if (dataInicio) {
      const inicioLimpo = new Date(
        dataInicio.getFullYear(),
        dataInicio.getMonth(),
        dataInicio.getDate(),
      );
      if (dataBase < inicioLimpo) return false;
    }
  }

  if (fim) {
    const dataFim = converterData(fim);
    if (dataFim) {
      const fimLimpo = new Date(
        dataFim.getFullYear(),
        dataFim.getMonth(),
        dataFim.getDate(),
      );
      if (dataBase > fimLimpo) return false;
    }
  }

  return true;
}

function pegarNomeTreinamento(treino) {
  return normalizarTexto(
    treino?.treinamentoNome || treino?.nome || treino?.tipo || "NÃO INFORMADO",
  );
}

function pegarObraTreinamento(treino, funcionario) {
  return normalizarTexto(
    treino?.obraNaData || treino?.obra || funcionario?.obra || "NÃO INFORMADA",
  );
}

function pegarFuncaoTreinamento(treino, funcionario) {
  return normalizarTexto(
    treino?.funcaoNaData ||
      treino?.funcao ||
      funcionario?.funcao ||
      "NÃO INFORMADA",
  );
}

function pegarResponsavel(treino) {
  return normalizarTexto(
    treino?.cadastradoPor ||
      treino?.cadastradopor ||
      treino?.atualizadoPor ||
      treino?.avaliador ||
      "NÃO INFORMADO",
  );
}

function pegarPrimeiroValor(objeto, chaves) {
  if (!objeto) return "";

  for (const chave of chaves) {
    const valor = objeto?.[chave];

    if (valor !== undefined && valor !== null && String(valor).trim() !== "") {
      return normalizarTexto(valor);
    }
  }

  return "";
}

function formatarRotuloAcao(acao = "") {
  const texto = normalizarTexto(acao);

  const mapa = {
    CADASTRO_TREINAMENTO: "CADASTRO DE TREINAMENTO",
    CADASTRO_TREINAMENTO_MATRIZ: "CADASTRO DE TREINAMENTO NA MATRIZ",
    EDICAO_TREINAMENTO_MATRIZ: "EDIÇÃO DE TREINAMENTO NA MATRIZ",
    EDICAO_TREINAMENTO_DA_MATRIZ: "EDIÇÃO DE TREINAMENTO DA MATRIZ",
    ATUALIZACAO_TREINAMENTO: "ATUALIZAÇÃO DE TREINAMENTO",
    CADASTRO_COLABORADOR: "CADASTRO DE COLABORADOR",
    EDICAO_COLABORADOR: "EDIÇÃO DE COLABORADOR",
    REATIVACAO_COLABORADOR: "REATIVAÇÃO DE COLABORADOR",
    DESATIVACAO_COLABORADOR: "DESATIVAÇÃO DE COLABORADOR",
    ATIVACAO_MATRIZ: "ATIVAÇÃO DE MATRIZ",
    DESATIVACAO_MATRIZ: "DESATIVAÇÃO DE MATRIZ",
  };

  return mapa[texto] || texto.replace(/_/g, " ");
}

function classificarAcaoAuditoria(acao = "") {
  const texto = normalizarTexto(acao);

  if (texto.includes("DESATIVACAO")) return "DESATIVACAO";
  if (texto.includes("REATIVACAO") || texto.includes("ATIVACAO")) {
    return "ATIVACAO";
  }
  if (
    texto.includes("EDICAO") ||
    texto.includes("ATUALIZACAO") ||
    texto.includes("ALTERACAO")
  ) {
    return "EDICAO";
  }
  if (texto.includes("CADASTRO") || texto.includes("CRIACAO")) {
    return "CADASTRO";
  }
  if (texto.includes("EXCLUSAO") || texto.includes("DELETE")) {
    return "EXCLUSAO";
  }

  return "OUTRA";
}

function compararCampo(rotulo, anterior, novo) {
  const antes = normalizarTexto(anterior || "");
  const depois = normalizarTexto(novo || "");

  if (!antes && !depois) return "";
  if (antes === depois) return "";

  return `${rotulo}: ${antes || "-"} → ${depois || "-"}`;
}

function detalhesGenericosOrganizados(detalhes = {}) {
  const partes = [];

  Object.entries(detalhes || {}).forEach(([chave, valor]) => {
    if (valor === undefined || valor === null || valor === "") return;
    if (typeof valor === "object") return;

    partes.push(`${normalizarTexto(chave)}: ${normalizarTexto(valor)}`);
  });

  return partes.join(" | ");
}

function formatarDetalhesAuditoria(item = {}) {
  const detalhes = item.detalhes || {};
  const acao = normalizarTexto(item.acao);

  if (detalhes.dadosAnteriores || detalhes.dadosNovos) {
    const anteriores = detalhes.dadosAnteriores || {};
    const novos = detalhes.dadosNovos || {};

    const colaborador =
      pegarPrimeiroValor(novos, ["nome", "colaboradorNome", "colaborador"]) ||
      pegarPrimeiroValor(anteriores, [
        "nome",
        "colaboradorNome",
        "colaborador",
      ]) ||
      pegarPrimeiroValor(detalhes, ["nome", "colaboradorNome", "colaborador"]);

    const alteracoes = [
      compararCampo("NOME", anteriores.nome, novos.nome),
      compararCampo("FUNÇÃO", anteriores.funcao, novos.funcao),
      compararCampo("OBRA", anteriores.obra, novos.obra),
      compararCampo("SITUAÇÃO", anteriores.situacao, novos.situacao),
      compararCampo("TREINAMENTO", anteriores.treinamento, novos.treinamento),
      compararCampo("VALIDADE", anteriores.validade, novos.validade),
      compararCampo("TIPO", anteriores.tipoPadrao, novos.tipoPadrao),
    ].filter(Boolean);

    return [
      colaborador ? `COLABORADOR: ${colaborador}` : "",
      alteracoes.length ? `ALTERAÇÕES: ${alteracoes.join("; ")}` : "",
    ]
      .filter(Boolean)
      .join(" | ");
  }

  if (acao.includes("COLABORADOR")) {
    const nome = pegarPrimeiroValor(detalhes, [
      "nome",
      "colaboradorNome",
      "colaborador",
    ]);
    const cpf = pegarPrimeiroValor(detalhes, ["cpf", "colaboradorCpf"]);
    const funcao = pegarPrimeiroValor(detalhes, ["funcao", "funcaoNaData"]);
    const obra = pegarPrimeiroValor(detalhes, ["obra", "obraNaData"]);
    const situacaoAnterior = pegarPrimeiroValor(detalhes, ["situacaoAnterior"]);
    const situacaoNova = pegarPrimeiroValor(detalhes, [
      "situacaoNova",
      "situacao",
    ]);

    return [
      nome ? `COLABORADOR: ${nome}` : "",
      cpf ? `CPF: ${cpf}` : "",
      obra ? `OBRA: ${obra}` : "",
      funcao ? `FUNÇÃO: ${funcao}` : "",
      situacaoAnterior || situacaoNova
        ? `SITUAÇÃO: ${situacaoAnterior || "-"} → ${situacaoNova || "-"}`
        : "",
    ]
      .filter(Boolean)
      .join(" | ");
  }

  if (
    acao.includes("TREINAMENTO") ||
    normalizarTexto(item.entidade).includes("TREINAMENTO")
  ) {
    const treinamento = pegarPrimeiroValor(detalhes, [
      "treinamento",
      "treinamentoNome",
      "nome",
    ]);
    const colaborador = pegarPrimeiroValor(detalhes, [
      "colaboradorNome",
      "colaborador",
      "nomeColaborador",
    ]);
    const cpf = pegarPrimeiroValor(detalhes, ["colaboradorCpf", "cpf"]);
    const obra = pegarPrimeiroValor(detalhes, ["obraNaData", "obra"]);
    const funcao = pegarPrimeiroValor(detalhes, ["funcaoNaData", "funcao"]);
    const data = pegarPrimeiroValor(detalhes, [
      "dataTreinamento",
      "novaData",
      "data",
    ]);
    const avaliador = pegarPrimeiroValor(detalhes, [
      "novoAvaliador",
      "avaliador",
    ]);

    return [
      treinamento ? `TREINAMENTO: ${treinamento}` : "",
      colaborador ? `COLABORADOR: ${colaborador}` : "",
      cpf ? `CPF: ${cpf}` : "",
      obra ? `OBRA: ${obra}` : "",
      funcao ? `FUNÇÃO: ${funcao}` : "",
      data ? `DATA: ${data}` : "",
      avaliador ? `AVALIADOR: ${avaliador}` : "",
    ]
      .filter(Boolean)
      .join(" | ");
  }

  if (
    acao.includes("MATRIZ") ||
    normalizarTexto(item.entidade).includes("MATRIZ")
  ) {
    const matriz = pegarPrimeiroValor(detalhes, ["matriz", "nome"]);
    const statusAnterior = pegarPrimeiroValor(detalhes, ["statusAnterior"]);
    const statusNovo = pegarPrimeiroValor(detalhes, ["statusNovo"]);

    return [
      matriz ? `MATRIZ: ${matriz}` : "",
      statusAnterior || statusNovo
        ? `SITUAÇÃO: ${statusAnterior || "-"} → ${statusNovo || "-"}`
        : "",
    ]
      .filter(Boolean)
      .join(" | ");
  }

  return detalhesGenericosOrganizados(detalhes) || "SEM DETALHES";
}

function pegarValidadeTreinamento(dados = {}) {
  const valor =
    dados?.validade ??
    dados?.validadeMeses ??
    dados?.validade_meses ??
    dados?.mesesValidade ??
    dados?.validadeEmMeses ??
    "";

  if (valor === undefined || valor === null || String(valor).trim() === "") {
    return "";
  }

  return valor;
}

function possuiVencimentoTreinamento(dados = {}) {
  if (dados?.possuiVencimento !== undefined) {
    return Boolean(dados.possuiVencimento);
  }

  const validade = pegarValidadeTreinamento(dados);
  return validade !== "";
}

function formatarValidadeMeses(valor, possuiVencimento = true) {
  if (!possuiVencimento) return "SEM VALIDADE";

  if (valor === undefined || valor === null || String(valor).trim() === "") {
    return "NÃO INFORMADA";
  }

  const numero = Number(valor);
  if (!Number.isNaN(numero)) {
    return numero === 1 ? "1 MÊS" : `${numero} MESES`;
  }

  return normalizarTexto(valor);
}

const Relatorios = () => {
  const navigate = useNavigate();

  const [tipoRelatorio, setTipoRelatorio] = useState("colaboradores_geral");
  const [filtroObra, setFiltroObra] = useState("Todas");
  const [filtroFuncao, setFiltroFuncao] = useState("Todas");
  const [filtroTipo, setFiltroTipo] = useState("Todos");
  const [filtroMatriz, setFiltroMatriz] = useState("Todas");
  const [busca, setBusca] = useState("");
  const [diasProximos, setDiasProximos] = useState(30);

  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [filtroAdministrador, setFiltroAdministrador] = useState("");
  const [filtroAcaoAuditoria, setFiltroAcaoAuditoria] = useState("TODAS");
  const [filtroEntidadeAuditoria, setFiltroEntidadeAuditoria] =
    useState("TODAS");
  const [periodoLancamento, setPeriodoLancamento] = useState("mensal");

  const [colunasColaboradores, setColunasColaboradores] = useState(
    OPCOES_COLABORADORES.map((item) => item.chave),
  );

  const [obras, setObras] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [nomesTreinamentos, setNomesTreinamentos] = useState([]);
  const [matrizes, setMatrizes] = useState([]);
  const [auditorias, setAuditorias] = useState([]);

  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  const [resultados, setResultados] = useState([]);
  const [erro, setErro] = useState("");

  const [catalogoDetalhes, setCatalogoDetalhes] = useState({});
  const [mostrarSugestoesColaborador, setMostrarSugestoesColaborador] =
    useState(false);
  const [mostrarSugestoesTreinamento, setMostrarSugestoesTreinamento] =
    useState(false);

  const [dashboard, setDashboard] = useState({
    anoAtual: new Date().getFullYear(),
    totalAnoAtual: 0,
    totalMesAtual: 0,
  });

  const wrapperSugestaoColaboradorRef = useRef(null);
  const wrapperSugestaoTreinamentoRef = useRef(null);

  const cacheTreinamentosRef = useRef({});
  const cacheRelatoriosRef = useRef({});
  const ultimaAssinaturaRelatorioRef = useRef(null);
  const colaboradorSelecionadoRef = useRef(null);
  const treinamentoSelecionadoRef = useRef(null);

  const buscarTreinamentosDoColaborador = useCallback(async (colaboradorId) => {
    if (cacheTreinamentosRef.current[colaboradorId]) {
      return cacheTreinamentosRef.current[colaboradorId];
    }

    const chaveCache = `treinamentos_${colaboradorId}`;
    const cacheSessao = lerCache(chaveCache);

    if (cacheSessao) {
      cacheTreinamentosRef.current[colaboradorId] = cacheSessao;
      return cacheSessao;
    }

    try {
      const snap = await getDocs(
        collection(db, "funcionarios", colaboradorId, "treinamentos"),
      );

      const lista = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      cacheTreinamentosRef.current[colaboradorId] = lista;
      salvarCache(chaveCache, lista);
      return lista;
    } catch (error) {
      console.error(
        `Erro ao buscar treinamentos do colaborador ${colaboradorId}:`,
        error,
      );
      return [];
    }
  }, []);

  const buscarTreinamentosDaFuncaoNaMatriz = useCallback(
    async (matrizId, funcao) => {
      if (!matrizId || !funcao) return [];

      try {
        const snap = await getDocs(
          collection(
            db,
            "matrizes",
            matrizId,
            "funcoes",
            normalizarTexto(funcao),
            "treinamentos",
          ),
        );

        return snap.docs
          .map((docItem) => {
            const dados = docItem.data();
            const validade = pegarValidadeTreinamento(dados);
            const possuiVencimento = possuiVencimentoTreinamento(dados);

            return {
              id: docItem.id,
              matriz: matrizId,
              ...dados,
              nome: normalizarTexto(dados?.nome || docItem.id),
              tipo: normalizarTexto(
                dados?.tipoPadrao || dados?.tipo || "OBRIGATORIO",
              ),
              validade,
              validadeMeses: validade,
              possuiVencimento,
              ativo: dados?.ativo !== undefined ? Boolean(dados?.ativo) : true,
            };
          })
          .filter((item) => item.ativo !== false);
      } catch (error) {
        console.error("Erro ao buscar matriz da função:", error);
        return [];
      }
    },
    [],
  );

  const buscarMatrizesDaObra = useCallback(async (obraNome) => {
    const obraNormalizada = normalizarTexto(obraNome);
    const chaveCache = `matrizes_obra_${obraNormalizada}`;
    const cacheSessao = lerCache(chaveCache);

    if (cacheSessao) return cacheSessao;

    try {
      const obrasSnap = await getDocs(collection(db, "obras_atuais"));

      const obraEncontrada = obrasSnap.docs
        .map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        .find(
          (obra) => normalizarTexto(obra.nome || obra.id) === obraNormalizada,
        );

      const matrizesAplicadas =
        obraEncontrada &&
        Array.isArray(obraEncontrada.matrizesAplicadas) &&
        obraEncontrada.matrizesAplicadas.length
          ? obraEncontrada.matrizesAplicadas
          : ["matriz_atlas"];

      salvarCache(chaveCache, matrizesAplicadas);
      return matrizesAplicadas;
    } catch (error) {
      console.error("Erro ao buscar matrizes da obra:", error);
      return ["matriz_atlas"];
    }
  }, []);

  const buscarTreinamentosEsperadosColaborador = useCallback(
    async (funcionario) => {
      const chaveCache = `esperados_${normalizarTexto(funcionario.obra)}_${normalizarTexto(funcionario.funcao)}`;
      const cacheSessao = lerCache(chaveCache);

      if (cacheSessao) return cacheSessao;

      const matrizesAplicadas = await buscarMatrizesDaObra(funcionario.obra);
      const mapa = new Map();

      for (const matrizId of matrizesAplicadas) {
        const lista = await buscarTreinamentosDaFuncaoNaMatriz(
          matrizId,
          funcionario.funcao,
        );

        lista.forEach((item) => {
          const chave = normalizarTexto(item.nome);
          if (!mapa.has(chave)) {
            mapa.set(chave, item);
          }
        });
      }

      const resultado = Array.from(mapa.values());
      salvarCache(chaveCache, resultado);
      return resultado;
    },
    [buscarMatrizesDaObra, buscarTreinamentosDaFuncaoNaMatriz],
  );

  useEffect(() => {
    async function carregarBase() {
      setLoadingBase(true);
      setErro("");

      try {
        const [
          obrasSnap,
          funcoesSnap,
          funcionariosSnap,
          matrizesSnap,
          auditoriaSnap,
        ] = await Promise.all([
          getDocs(collection(db, "obras_atuais")),
          getDocs(collection(db, "catalogo_funcoes")),
          getDocs(collection(db, "funcionarios")),
          getDocs(collection(db, "matrizes")),
          getDocs(collection(db, "auditoria_sistema")),
        ]);

        const listaObras = obrasSnap.docs
          .map((doc) => normalizarTexto(doc.data()?.nome || doc.id))
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "pt-BR"));

        const listaFuncoes = funcoesSnap.docs
          .map((doc) => normalizarTexto(doc.data()?.nome || doc.id))
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "pt-BR"));

        const listaColaboradores = funcionariosSnap.docs
          .map((doc) => {
            const dados = doc.data();
            return {
              id: doc.id,
              nome: normalizarTexto(dados?.nome || ""),
              cpf: dados?.cpf || doc.id || "",
              obra: normalizarTexto(dados?.obra || ""),
              funcao: normalizarTexto(dados?.funcao || ""),
              situacao: normalizarTexto(dados?.situacao || ""),
            };
          })
          .filter((item) => item.nome)
          .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

        const listaMatrizes = [];

        for (const matrizDoc of matrizesSnap.docs) {
          const matrizDados = matrizDoc.data();

          const treinamentosSnap = await getDocs(
            collection(db, "matrizes", matrizDoc.id, "treinamentos"),
          );

          const treinamentos = treinamentosSnap.docs.map((treinamentoDoc) => {
            const dados = treinamentoDoc.data();
            const validade = pegarValidadeTreinamento(dados);
            const possuiVencimento = possuiVencimentoTreinamento(dados);

            return {
              id: treinamentoDoc.id,
              ...dados,
              nome: normalizarTexto(dados?.nome || treinamentoDoc.id),
              tipo: normalizarTexto(
                dados?.tipoPadrao || dados?.tipo || "OBRIGATORIO",
              ),
              validade,
              validadeMeses: validade,
              possuiVencimento,
              ativo: dados?.ativo !== undefined ? Boolean(dados?.ativo) : true,
            };
          });

          listaMatrizes.push({
            id: matrizDoc.id,
            nome: normalizarTexto(matrizDados?.nome || matrizDoc.id),
            tipo: normalizarTexto(matrizDados?.tipo || ""),
            ativa:
              matrizDados?.ativa !== undefined
                ? Boolean(matrizDados?.ativa)
                : true,
            treinamentos,
          });
        }

        const listaAuditoria = auditoriaSnap.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
          acao: normalizarTexto(docItem.data()?.acao || ""),
          entidade: normalizarTexto(docItem.data()?.entidade || ""),
          entidadeId: normalizarTexto(docItem.data()?.entidadeId || ""),
          responsavelNome: normalizarTexto(
            docItem.data()?.responsavelNome || "",
          ),
          dataHoraOriginal: docItem.data()?.dataHora || "",
          dataHora: converterData(docItem.data()?.dataHora),
        }));

        const mapaDetalhes = {};
        const nomesTreinamentosSet = new Set();

        listaMatrizes.forEach((matriz) => {
          matriz.treinamentos.forEach((treinamento) => {
            const nome = normalizarTexto(treinamento.nome);
            if (!nome) return;

            nomesTreinamentosSet.add(nome);

            mapaDetalhes[nome] = {
              validade: treinamento?.validade ?? "",
              possuiVencimento: treinamento?.possuiVencimento,
            };
          });
        });

        setObras([...new Set(listaObras)]);
        setFuncoes([...new Set(listaFuncoes)]);
        setCatalogoDetalhes(mapaDetalhes);
        setColaboradores(listaColaboradores);
        setMatrizes(listaMatrizes);
        setAuditorias(listaAuditoria);
        setNomesTreinamentos(
          Array.from(nomesTreinamentosSet).sort((a, b) =>
            a.localeCompare(b, "pt-BR"),
          ),
        );
      } catch (error) {
        console.error(error);
        setErro("Erro ao carregar dados base do Firestore.");
      } finally {
        setLoadingBase(false);
      }
    }

    carregarBase();
  }, []);

  useEffect(() => {
    function handleClickFora(event) {
      if (
        wrapperSugestaoColaboradorRef.current &&
        !wrapperSugestaoColaboradorRef.current.contains(event.target)
      ) {
        setMostrarSugestoesColaborador(false);
      }

      if (
        wrapperSugestaoTreinamentoRef.current &&
        !wrapperSugestaoTreinamentoRef.current.contains(event.target)
      ) {
        setMostrarSugestoesTreinamento(false);
      }
    }

    document.addEventListener("mousedown", handleClickFora);
    return () => document.removeEventListener("mousedown", handleClickFora);
  }, []);

  const carregarDashboard = useCallback(async () => {
    if (loadingBase) return;

    setLoadingDashboard(true);

    try {
      if (tipoRelatorio === "matrizes") {
        const matrizesFiltradas =
          filtroMatriz === "Todas"
            ? matrizes
            : matrizes.filter((matriz) => matriz.id === filtroMatriz);

        setDashboard({
          anoAtual: new Date().getFullYear(),
          totalAnoAtual: matrizesFiltradas.length,
          totalMesAtual: matrizesFiltradas.reduce(
            (total, matriz) => total + matriz.treinamentos.length,
            0,
          ),
        });
        return;
      }

      if (colaboradores.length === 0) return;

      const agora = new Date();
      const anoAtual = agora.getFullYear();
      const mesAtual = agora.getMonth();

      let totalAnoAtual = 0;
      let totalMesAtual = 0;

      const colaboradoresFiltrados = colaboradores.filter((colaborador) => {
        if (filtroObra !== "Todas" && colaborador.obra !== filtroObra) {
          return false;
        }
        return true;
      });

      const listasTreinamentos = await Promise.all(
        colaboradoresFiltrados.map((colaborador) =>
          buscarTreinamentosDoColaborador(colaborador.id),
        ),
      );

      listasTreinamentos.forEach((lista) => {
        filtrarLancamentosOriginais(lista).forEach((treino) => {
          const dataLancamento = pegarDataLancamento(treino);
          if (!dataLancamento) return;

          if (dataLancamento.getFullYear() === anoAtual) {
            totalAnoAtual += 1;
          }

          if (
            dataLancamento.getFullYear() === anoAtual &&
            dataLancamento.getMonth() === mesAtual
          ) {
            totalMesAtual += 1;
          }
        });
      });

      setDashboard({
        anoAtual,
        totalAnoAtual,
        totalMesAtual,
      });
    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
    } finally {
      setLoadingDashboard(false);
    }
  }, [
    buscarTreinamentosDoColaborador,
    colaboradores,
    filtroMatriz,
    filtroObra,
    loadingBase,
    matrizes,
    tipoRelatorio,
  ]);

  useEffect(() => {
    carregarDashboard();
  }, [carregarDashboard]);

  function obterDetalhesTreinamento(nomeTreinamento, treinoRealizado = null) {
    const catalogo = catalogoDetalhes[normalizarTexto(nomeTreinamento)] || {};

    const possuiVencimento =
      treinoRealizado?.possuiVencimento !== undefined
        ? Boolean(treinoRealizado?.possuiVencimento)
        : catalogo?.possuiVencimento !== undefined
          ? Boolean(catalogo?.possuiVencimento)
          : true;

    const validadeMeses =
      treinoRealizado?.validadeMeses !== undefined &&
      treinoRealizado?.validadeMeses !== null &&
      treinoRealizado?.validadeMeses !== ""
        ? Number(treinoRealizado?.validadeMeses)
        : catalogo?.validade !== undefined &&
            catalogo?.validade !== null &&
            catalogo?.validade !== ""
          ? Number(catalogo?.validade)
          : null;

    return {
      possuiVencimento,
      validadeMeses,
    };
  }

  function passaFiltroTipo(tipoTreinamento) {
    if (filtroTipo === "Todos") return true;
    return normalizarTexto(tipoTreinamento) === normalizarTexto(filtroTipo);
  }

  function selecionarColaborador(colaborador) {
    colaboradorSelecionadoRef.current = colaborador;
    setBusca(colaborador.nome);
    setMostrarSugestoesColaborador(false);
  }

  function selecionarTreinamento(nomeTreinamento) {
    treinamentoSelecionadoRef.current = nomeTreinamento;
    setBusca(nomeTreinamento);
    setMostrarSugestoesTreinamento(false);
  }

  function handleBuscaChange(e) {
    const valor = textoMaiusculo(e.target.value);
    setBusca(valor);

    if (tipoRelatorio === "colaborador") {
      setMostrarSugestoesColaborador(true);
      const termo = normalizarTexto(valor);

      const encontrados = colaboradores.filter((item) => {
        const bateNome = normalizarTexto(item.nome) === termo;
        const bateCpf = normalizarTexto(item.cpf) === termo;
        return bateNome || bateCpf;
      });

      if (encontrados.length === 1) {
        selecionarColaborador(encontrados[0]);
      } else {
        colaboradorSelecionadoRef.current = null;
      }
    }

    if (tipoRelatorio === "treinamento") {
      setMostrarSugestoesTreinamento(true);
      treinamentoSelecionadoRef.current = null;
    }

    if (tipoRelatorio === "auditoria") {
      setMostrarSugestoesColaborador(true);
    }
  }

  const sugestoesColaboradores = useMemo(() => {
    if (tipoRelatorio !== "colaborador" && tipoRelatorio !== "auditoria") {
      return [];
    }
    if (!busca.trim()) return [];

    const termo = normalizarTexto(busca);

    return colaboradores
      .filter((item) => {
        const bateNome = normalizarTexto(item.nome).includes(termo);
        const bateCpf = normalizarTexto(item.cpf).includes(termo);
        return bateNome || bateCpf;
      })
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
      .slice(0, 8);
  }, [busca, colaboradores, tipoRelatorio]);

  const sugestoesTreinamentos = useMemo(() => {
    if (tipoRelatorio !== "treinamento") return [];
    if (!busca.trim()) return [];

    const termo = normalizarTexto(busca);

    return nomesTreinamentos
      .filter((nome) => normalizarTexto(nome).includes(termo))
      .sort((a, b) => a.localeCompare(b, "pt-BR"))
      .slice(0, 8);
  }, [busca, nomesTreinamentos, tipoRelatorio]);

  async function gerarRelatorioColaboradoresGeral() {
    const listaFinal = [];

    const colaboradoresBase = colaboradores.filter((funcionario) => {
      if (filtroObra !== "Todas" && funcionario.obra !== filtroObra)
        return false;
      if (filtroFuncao !== "Todas" && funcionario.funcao !== filtroFuncao)
        return false;
      return true;
    });

    for (const funcionario of colaboradoresBase) {
      const treinamentosEsperados =
        await buscarTreinamentosEsperadosColaborador(funcionario);

      const treinamentosRealizados = filtrarTreinamentosValidos(
        await buscarTreinamentosDoColaborador(funcionario.id),
      );

      const lancamentosOriginais = filtrarLancamentosOriginais(
        treinamentosRealizados,
      );

      const realizadosMap = new Map();

      treinamentosRealizados.forEach((treino) => {
        realizadosMap.set(
          normalizarTexto(pegarNomeTreinamento(treino)),
          treino,
        );
      });

      const pendentes = treinamentosEsperados.filter(
        (item) => !realizadosMap.has(normalizarTexto(item.nome)),
      );

      listaFinal.push({
        id: funcionario.id,
        nome: funcionario.nome,
        cpf: funcionario.cpf,
        obra: funcionario.obra,
        funcao: funcionario.funcao,
        situacao: funcionario.situacao || "ATIVO",
        quantidadeLancados: lancamentosOriginais.length,
        quantidadePendentes: pendentes.length,
      });
    }

    setResultados(listaFinal);
  }

  async function gerarRelatorioCompletoPorColaborador() {
    if (!busca.trim()) {
      alert("Digite o nome ou CPF do colaborador.");
      return;
    }

    if (!colaboradorSelecionadoRef.current) {
      alert("Selecione um colaborador da lista de sugestões.");
      return;
    }

    const funcionario = colaboradorSelecionadoRef.current;
    const matrizFuncao =
      await buscarTreinamentosEsperadosColaborador(funcionario);
    const treinamentosLista = filtrarTreinamentosValidos(
      await buscarTreinamentosDoColaborador(funcionario.id),
    );

    const realizadosMap = {};

    treinamentosLista.forEach((treino) => {
      const nomeTreinamento = pegarNomeTreinamento(treino);
      const chave = normalizarTexto(nomeTreinamento);

      if (nomeTreinamento) {
        realizadosMap[chave] = {
          idTreinamento: treino.id,
          nome: nomeTreinamento,
          data: treino?.data || "",
          avaliador: treino?.avaliador || "",
          validadeMeses: treino?.validadeMeses ?? "",
          possuiVencimento: treino?.possuiVencimento,
          cadastradoPor: treino?.cadastradoPor || "",
        };
      }
    });

    const listaFinal = [];

    matrizFuncao.forEach((itemMatriz) => {
      const chaveTreinamento = normalizarTexto(itemMatriz.nome);
      const treinoRealizado = realizadosMap[chaveTreinamento] || null;

      if (!passaFiltroTipo(itemMatriz.tipo)) return;

      const dataRealizacao = converterData(treinoRealizado?.data);
      const detalhes = obterDetalhesTreinamento(
        itemMatriz.nome,
        treinoRealizado,
      );

      const vencimento =
        dataRealizacao && detalhes.possuiVencimento
          ? adicionarMeses(dataRealizacao, detalhes.validadeMeses)
          : null;

      listaFinal.push({
        id: `${funcionario.id}-${chaveTreinamento}`,
        funcionarioId: funcionario.id,
        colaborador: funcionario.nome || "",
        cpf: funcionario.cpf || "",
        obra: funcionario.obra || "",
        funcao: funcionario.funcao || "",
        treinamento: itemMatriz.nome || "",
        tipoTreinamento: itemMatriz.tipo || "",
        realizado: !!treinoRealizado,
        avaliador: treinoRealizado?.avaliador || "",
        cadastradoPor: treinoRealizado?.cadastradoPor || "",
        dataRealizacao: dataRealizacao ? formatarDataBR(dataRealizacao) : "",
        validadeMeses: detalhes.possuiVencimento
          ? (detalhes.validadeMeses ?? "")
          : "",
        possuiVencimento: detalhes.possuiVencimento,
        vencimento: vencimento ? formatarDataBR(vencimento) : "",
        situacao: calcularSituacao(
          vencimento,
          !!treinoRealizado,
          diasProximos,
          detalhes.possuiVencimento,
        ),
      });
    });

    listaFinal.sort((a, b) =>
      a.treinamento.localeCompare(b.treinamento, "pt-BR"),
    );

    setResultados(listaFinal);
  }

  async function gerarRelatorioPorTreinamento() {
    if (!busca.trim()) {
      alert("Digite o nome do treinamento.");
      return;
    }

    const termoBusca = normalizarTexto(
      treinamentoSelecionadoRef.current || busca,
    );

    const colaboradoresBase = colaboradores.filter((funcionario) => {
      if (filtroObra !== "Todas" && funcionario.obra !== filtroObra)
        return false;
      if (filtroFuncao !== "Todas" && funcionario.funcao !== filtroFuncao)
        return false;
      return true;
    });

    const listaFinal = [];

    for (const funcionario of colaboradoresBase) {
      const matrizFuncao =
        await buscarTreinamentosEsperadosColaborador(funcionario);
      const treinamentosLista = filtrarTreinamentosValidos(
        await buscarTreinamentosDoColaborador(funcionario.id),
      );

      treinamentosLista.forEach((treino) => {
        const nomeTreinamento = pegarNomeTreinamento(treino);

        if (!normalizarTexto(nomeTreinamento).includes(termoBusca)) return;

        const itemMatriz = matrizFuncao.find(
          (item) =>
            normalizarTexto(item.nome) === normalizarTexto(nomeTreinamento),
        );
        if (!itemMatriz) return;

        const dataRealizacao = converterData(treino?.data);
        const detalhes = obterDetalhesTreinamento(nomeTreinamento, treino);

        const vencimento =
          dataRealizacao && detalhes.possuiVencimento
            ? adicionarMeses(dataRealizacao, detalhes.validadeMeses)
            : null;

        listaFinal.push({
          id: `${funcionario.id}-${treino.id}`,
          funcionarioId: funcionario.id,
          colaborador: funcionario.nome || "",
          cpf: funcionario.cpf || "",
          obra: funcionario.obra || "",
          funcao: funcionario.funcao || "",
          treinamento: nomeTreinamento,
          tipoTreinamento: itemMatriz.tipo || "",
          realizado: true,
          avaliador: treino?.avaliador || "",
          cadastradoPor: treino?.cadastradoPor || "",
          dataRealizacao: dataRealizacao ? formatarDataBR(dataRealizacao) : "",
          validadeMeses: detalhes.possuiVencimento
            ? (detalhes.validadeMeses ?? "")
            : "",
          possuiVencimento: detalhes.possuiVencimento,
          vencimento: vencimento ? formatarDataBR(vencimento) : "",
          situacao: calcularSituacao(
            vencimento,
            true,
            diasProximos,
            detalhes.possuiVencimento,
          ),
        });
      });
    }

    listaFinal.sort((a, b) =>
      a.colaborador.localeCompare(b.colaborador, "pt-BR"),
    );

    setResultados(listaFinal);
  }

  async function gerarRelatorioVencidosOuProximos(modo = "vencidos") {
    const colaboradoresBase = colaboradores.filter((funcionario) => {
      if (filtroObra !== "Todas" && funcionario.obra !== filtroObra)
        return false;
      if (filtroFuncao !== "Todas" && funcionario.funcao !== filtroFuncao)
        return false;
      return true;
    });

    const listaFinal = [];

    for (const funcionario of colaboradoresBase) {
      const matrizFuncao =
        await buscarTreinamentosEsperadosColaborador(funcionario);
      const treinamentosLista = filtrarTreinamentosValidos(
        await buscarTreinamentosDoColaborador(funcionario.id),
      );

      treinamentosLista.forEach((treino) => {
        const nomeTreinamento = pegarNomeTreinamento(treino);
        const itemMatriz = matrizFuncao.find(
          (item) =>
            normalizarTexto(item.nome) === normalizarTexto(nomeTreinamento),
        );

        if (!itemMatriz) return;
        if (!passaFiltroTipo(itemMatriz.tipo)) return;

        const dataRealizacao = converterData(treino?.data);
        const detalhes = obterDetalhesTreinamento(nomeTreinamento, treino);

        if (!detalhes.possuiVencimento) return;

        const vencimentoData = dataRealizacao
          ? adicionarMeses(dataRealizacao, detalhes.validadeMeses)
          : null;

        if (!vencimentoData) return;

        const dias = diferencaEmDias(vencimentoData);
        const situacao = calcularSituacao(
          vencimentoData,
          true,
          diasProximos,
          detalhes.possuiVencimento,
        );

        if (modo === "vencidos" && situacao !== "Vencido") return;

        if (
          modo === "proximos" &&
          !(
            situacao === "Próximo do vencimento" ||
            (dias !== null && dias >= 0 && dias <= Number(diasProximos))
          )
        ) {
          return;
        }

        listaFinal.push({
          id: `${funcionario.id}-${treino.id}`,
          funcionarioId: funcionario.id,
          colaborador: funcionario.nome || "",
          cpf: funcionario.cpf || "",
          obra: funcionario.obra || "",
          funcao: funcionario.funcao || "",
          treinamento: nomeTreinamento,
          tipoTreinamento: itemMatriz.tipo || "",
          realizado: true,
          avaliador: treino?.avaliador || "",
          cadastradoPor: treino?.cadastradoPor || "",
          dataRealizacao: dataRealizacao ? formatarDataBR(dataRealizacao) : "",
          validadeMeses: detalhes.validadeMeses ?? "",
          possuiVencimento: detalhes.possuiVencimento,
          vencimento: vencimentoData ? formatarDataBR(vencimentoData) : "",
          situacao,
          diasRestantes: dias,
        });
      });
    }

    listaFinal.sort((a, b) => {
      const porColaborador = a.colaborador.localeCompare(
        b.colaborador,
        "pt-BR",
      );
      if (porColaborador !== 0) return porColaborador;
      return a.treinamento.localeCompare(b.treinamento, "pt-BR");
    });

    setResultados(listaFinal);
  }

  async function gerarRelatorioMatrizes() {
    const listaFinal = [];

    matrizes.forEach((matriz) => {
      if (filtroMatriz !== "Todas" && matriz.id !== filtroMatriz) {
        return;
      }

      matriz.treinamentos.forEach((treinamento) => {
        if (filtroTipo !== "Todos" && !passaFiltroTipo(treinamento.tipo)) {
          return;
        }

        const possuiVencimento = treinamento.possuiVencimento !== false;
        const validadeBruta = pegarValidadeTreinamento(treinamento);

        listaFinal.push({
          id: `${matriz.id}-${treinamento.id}`,
          matrizId: matriz.id,
          matriz: matriz.nome,
          matrizAtiva: matriz.ativa ? "ATIVA" : "DESATIVADA",
          treinamento: treinamento.nome,
          tipoTreinamento: treinamento.tipo,
          validadeMeses: formatarValidadeMeses(validadeBruta, possuiVencimento),
          possuiVencimento,
          treinamentoAtivo: treinamento.ativo ? "ATIVO" : "DESATIVADO",
        });
      });
    });

    listaFinal.sort((a, b) => {
      const ordemMatriz = a.matriz.localeCompare(b.matriz, "pt-BR");
      if (ordemMatriz !== 0) return ordemMatriz;
      return a.treinamento.localeCompare(b.treinamento, "pt-BR");
    });

    setResultados(listaFinal);
  }

  async function gerarRelatorioAuditoria() {
    const termoAdministrador = normalizarTexto(filtroAdministrador);
    const termoBusca = normalizarTexto(busca);
    const entidadeSelecionada = normalizarTexto(filtroEntidadeAuditoria);
    const acaoSelecionada = normalizarTexto(filtroAcaoAuditoria);

    const listaFinal = auditorias
      .filter((item) => {
        if (dataInicio || dataFim) {
          if (!estaNoPeriodo(item.dataHora, dataInicio, dataFim)) return false;
        }

        const classeAcao = classificarAcaoAuditoria(item.acao);
        const entidadeItem = normalizarTexto(item.entidade);

        if (acaoSelecionada !== "TODAS" && classeAcao !== acaoSelecionada) {
          return false;
        }

        if (
          entidadeSelecionada !== "TODAS" &&
          entidadeItem !== entidadeSelecionada
        ) {
          return false;
        }

        if (
          termoAdministrador &&
          !normalizarTexto(item.responsavelNome).includes(termoAdministrador)
        ) {
          return false;
        }

        if (termoBusca) {
          const detalhesTexto = normalizarTexto(
            JSON.stringify(item.detalhes || {}),
          );

          const encontrou =
            normalizarTexto(item.entidadeId).includes(termoBusca) ||
            detalhesTexto.includes(termoBusca) ||
            normalizarTexto(item.acao).includes(termoBusca) ||
            normalizarTexto(item.entidade).includes(termoBusca) ||
            normalizarTexto(item.responsavelNome).includes(termoBusca);

          if (!encontrou) return false;
        }

        if (filtroObra !== "Todas") {
          const detalhesTexto = normalizarTexto(
            JSON.stringify(item.detalhes || {}),
          );
          if (!detalhesTexto.includes(normalizarTexto(filtroObra))) {
            return false;
          }
        }

        return true;
      })
      .map((item) => {
        const detalhesResumo = formatarDetalhesAuditoria(item);

        return {
          id: item.id,
          dataHora: item.dataHora ? formatarDataBR(item.dataHora) : "",
          dataOrdenacao: item.dataHora?.getTime?.() || 0,
          acao: formatarRotuloAcao(item.acao),
          entidade: item.entidade,
          responsavelNome: item.responsavelNome,
          detalhesResumo,
          detalhesLista: detalhesResumo
            .split(" | ")
            .map((parte) => parte.trim())
            .filter(Boolean),
        };
      })
      .sort((a, b) => b.dataOrdenacao - a.dataOrdenacao);

    setResultados(listaFinal);
  }

  async function gerarRelatorioLancamentos() {
    const lista = [];

    const colaboradoresBase = colaboradores.filter((funcionario) => {
      if (filtroObra !== "Todas" && funcionario.obra !== filtroObra)
        return false;
      if (filtroFuncao !== "Todas" && funcionario.funcao !== filtroFuncao)
        return false;
      return true;
    });

    for (const funcionario of colaboradoresBase) {
      const treinamentos = filtrarLancamentosOriginais(
        await buscarTreinamentosDoColaborador(funcionario.id),
      );

      treinamentos.forEach((treino) => {
        const dataLancamento = pegarDataLancamento(treino);

        if (!estaNoPeriodo(dataLancamento, dataInicio, dataFim)) return;

        const responsavel = pegarResponsavel(treino);

        if (
          filtroAdministrador &&
          !responsavel.includes(normalizarTexto(filtroAdministrador))
        ) {
          return;
        }

        lista.push({
          id: `${funcionario.id}-${treino.id}`,
          periodo: formatarPeriodoLancamento(dataLancamento, periodoLancamento),
          colaborador: funcionario.nome,
          cpf: funcionario.cpf,
          obra: pegarObraTreinamento(treino, funcionario),
          funcao: pegarFuncaoTreinamento(treino, funcionario),
          treinamento: pegarNomeTreinamento(treino),
          cadastradoPor: responsavel,
          dataRealizacao: converterData(treino.data)
            ? formatarDataBR(converterData(treino.data))
            : "",
          dataLancamento: dataLancamento ? formatarDataBR(dataLancamento) : "",
        });
      });
    }

    lista.sort((a, b) => {
      const ordemPeriodo = a.periodo.localeCompare(b.periodo, "pt-BR");
      if (ordemPeriodo !== 0) return ordemPeriodo;
      return a.colaborador.localeCompare(b.colaborador, "pt-BR");
    });

    setResultados(lista);
  }

  function criarAssinaturaRelatorio() {
    return JSON.stringify({
      tipoRelatorio,
      filtroObra,
      filtroFuncao,
      filtroTipo,
      filtroMatriz,
      busca: normalizarTexto(busca),
      diasProximos,
      dataInicio,
      dataFim,
      filtroAdministrador: normalizarTexto(filtroAdministrador),
      filtroAcaoAuditoria,
      filtroEntidadeAuditoria,
      periodoLancamento,
      colunasColaboradores,
      colaboradorSelecionadoId: colaboradorSelecionadoRef.current?.id || "",
      treinamentoSelecionado: treinamentoSelecionadoRef.current || "",
    });
  }

  function tentarUsarRelatorioEmCache(assinatura) {
    if (cacheRelatoriosRef.current[assinatura]) {
      setResultados(cacheRelatoriosRef.current[assinatura]);
      return true;
    }

    const cacheSessao = lerCache(`relatorio_${assinatura}`);

    if (cacheSessao) {
      cacheRelatoriosRef.current[assinatura] = cacheSessao;
      setResultados(cacheSessao);
      return true;
    }

    return false;
  }

  function salvarRelatorioEmCache(assinatura, dados) {
    cacheRelatoriosRef.current[assinatura] = dados;
    salvarCache(`relatorio_${assinatura}`, dados);
  }

  async function gerarRelatorio() {
    const assinatura = criarAssinaturaRelatorio();

    setLoadingRelatorio(true);
    setErro("");
    setResultados([]);
    setMostrarSugestoesColaborador(false);
    setMostrarSugestoesTreinamento(false);

    try {
      if (tentarUsarRelatorioEmCache(assinatura)) return;

      ultimaAssinaturaRelatorioRef.current = assinatura;

      if (tipoRelatorio === "colaboradores_geral") {
        await gerarRelatorioColaboradoresGeral();
      }

      if (tipoRelatorio === "colaborador") {
        await gerarRelatorioCompletoPorColaborador();
      }

      if (tipoRelatorio === "treinamento") {
        await gerarRelatorioPorTreinamento();
      }

      if (tipoRelatorio === "vencidos") {
        await gerarRelatorioVencidosOuProximos("vencidos");
      }

      if (tipoRelatorio === "proximos") {
        await gerarRelatorioVencidosOuProximos("proximos");
      }

      if (tipoRelatorio === "matrizes") {
        await gerarRelatorioMatrizes();
      }

      if (tipoRelatorio === "auditoria") {
        await gerarRelatorioAuditoria();
      }

      if (tipoRelatorio === "lancamentos") {
        await gerarRelatorioLancamentos();
      }
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar relatório.");
    } finally {
      setLoadingRelatorio(false);
    }
  }

  function limparFiltros() {
    colaboradorSelecionadoRef.current = null;
    treinamentoSelecionadoRef.current = null;
    setBusca("");
    setFiltroObra("Todas");
    setFiltroFuncao("Todas");
    setFiltroTipo("Todos");
    setFiltroMatriz("Todas");
    setDiasProximos(30);
    setDataInicio("");
    setDataFim("");
    setFiltroAdministrador("");
    setFiltroAcaoAuditoria("TODAS");
    setFiltroEntidadeAuditoria("TODAS");
    setPeriodoLancamento("mensal");
    setColunasColaboradores(OPCOES_COLABORADORES.map((item) => item.chave));
    setResultados([]);
    setErro("");
    setMostrarSugestoesColaborador(false);
    setMostrarSugestoesTreinamento(false);
  }

  const resumo = useMemo(() => {
    if (tipoRelatorio === "matrizes") {
      const matrizesUnicas = new Set(resultados.map((item) => item.matrizId));

      return {
        total: resultados.length,
        matrizes: matrizesUnicas.size,
        obrigatorios: resultados.filter(
          (item) => normalizarTexto(item.tipoTreinamento) === "OBRIGATORIO",
        ).length,
        eventuais: resultados.filter(
          (item) => normalizarTexto(item.tipoTreinamento) === "EVENTUAL",
        ).length,
        comValidade: resultados.filter(
          (item) =>
            item.possuiVencimento &&
            item.validadeMeses !== "NÃO INFORMADA" &&
            item.validadeMeses !== "SEM VALIDADE",
        ).length,
        semValidade: resultados.filter((item) => !item.possuiVencimento).length,
      };
    }

    return {
      total: resultados.length,
      realizados: resultados.filter((item) => item.realizado).length,
      pendentes: resultados.filter((item) => item.situacao === "Pendente")
        .length,
      vencidos: resultados.filter((item) => item.situacao === "Vencido").length,
      proximos: resultados.filter(
        (item) => item.situacao === "Próximo do vencimento",
      ).length,
      emDia: resultados.filter((item) => item.situacao === "Em dia").length,
    };
  }, [resultados, tipoRelatorio]);

  useEffect(() => {
    if (!ultimaAssinaturaRelatorioRef.current || !resultados.length) return;
    salvarRelatorioEmCache(ultimaAssinaturaRelatorioRef.current, resultados);
  }, [resultados]);

  const dadosCabecalhoColaborador = useMemo(() => {
    if (tipoRelatorio !== "colaborador") return null;
    if (!resultados.length) return null;

    return {
      colaborador: resultados[0].colaborador || "",
      cpf: resultados[0].cpf || "",
      obra: resultados[0].obra || "",
      funcao: resultados[0].funcao || "",
    };
  }, [resultados, tipoRelatorio]);

  const tituloBusca = useMemo(() => {
    if (tipoRelatorio === "colaborador") return "Nome ou CPF do colaborador";
    if (tipoRelatorio === "treinamento") return "Nome do treinamento";
    if (tipoRelatorio === "auditoria") return "Colaborador ou texto livre";
    return "Busca";
  }, [tipoRelatorio]);

  const placeholderBusca = useMemo(() => {
    if (tipoRelatorio === "colaborador") {
      return "DIGITE O NOME OU CPF DO COLABORADOR";
    }

    if (tipoRelatorio === "treinamento") {
      return "DIGITE O NOME DO TREINAMENTO";
    }

    if (tipoRelatorio === "auditoria") {
      return "DIGITE O COLABORADOR OU UMA PALAVRA-CHAVE";
    }

    return "";
  }, [tipoRelatorio]);

  const mostrarBusca =
    tipoRelatorio === "colaborador" ||
    tipoRelatorio === "treinamento" ||
    tipoRelatorio === "auditoria";

  const mostrarFiltroObra =
    tipoRelatorio === "colaboradores_geral" ||
    tipoRelatorio === "treinamento" ||
    tipoRelatorio === "vencidos" ||
    tipoRelatorio === "proximos" ||
    tipoRelatorio === "auditoria" ||
    tipoRelatorio === "lancamentos";

  const mostrarFiltroFuncao =
    tipoRelatorio === "colaboradores_geral" ||
    tipoRelatorio === "treinamento" ||
    tipoRelatorio === "vencidos" ||
    tipoRelatorio === "proximos" ||
    tipoRelatorio === "lancamentos";

  const mostrarFiltroTipo =
    tipoRelatorio === "colaborador" ||
    tipoRelatorio === "vencidos" ||
    tipoRelatorio === "proximos" ||
    tipoRelatorio === "matrizes";

  const mostrarFiltroDias =
    tipoRelatorio === "colaborador" || tipoRelatorio === "proximos";

  const mostrarFiltroPeriodo =
    tipoRelatorio === "auditoria" || tipoRelatorio === "lancamentos";

  const mostrarFiltroAdministrador =
    tipoRelatorio === "auditoria" || tipoRelatorio === "lancamentos";

  const mostrarFiltroLancamento = tipoRelatorio === "lancamentos";

  const mostrarColunasColaboradores = tipoRelatorio === "colaboradores_geral";

  const colunasTabela = useMemo(() => {
    if (tipoRelatorio === "colaboradores_geral") {
      return colunasColaboradores.map((chave) => {
        const item = OPCOES_COLABORADORES.find(
          (opcao) => opcao.chave === chave,
        );
        return { chave, label: item?.label || chave };
      });
    }

    if (tipoRelatorio === "matrizes") {
      return [
        { chave: "matriz", label: "Matriz" },
        { chave: "matrizAtiva", label: "Situação matriz" },
        { chave: "treinamento", label: "Treinamento" },
        { chave: "tipoTreinamento", label: "Tipo" },
        { chave: "validadeMeses", label: "Validade" },
        { chave: "treinamentoAtivo", label: "Situação treinamento" },
      ];
    }

    if (tipoRelatorio === "auditoria") {
      return [
        { chave: "dataHora", label: "Data" },
        { chave: "acao", label: "Ação" },
        { chave: "entidade", label: "Entidade" },
        { chave: "responsavelNome", label: "Responsável" },
        { chave: "detalhesResumo", label: "Detalhes" },
      ];
    }

    if (tipoRelatorio === "lancamentos") {
      return [
        { chave: "periodo", label: "Período" },
        { chave: "colaborador", label: "Colaborador" },
        { chave: "cpf", label: "CPF" },
        { chave: "obra", label: "Obra" },
        { chave: "funcao", label: "Função" },
        { chave: "treinamento", label: "Treinamento" },
        { chave: "cadastradoPor", label: "Cadastrado por" },
        { chave: "dataRealizacao", label: "Data treinamento" },
        { chave: "dataLancamento", label: "Data lançamento" },
      ];
    }

    return [
      ...(tipoRelatorio !== "colaborador"
        ? [
            { chave: "colaborador", label: "Colaborador" },
            { chave: "cpf", label: "CPF" },
            { chave: "obra", label: "Obra" },
            { chave: "funcao", label: "Função" },
          ]
        : []),
      { chave: "treinamento", label: "Treinamento" },
      { chave: "tipoTreinamento", label: "Tipo" },
      { chave: "realizado", label: "Realizado" },
      { chave: "dataRealizacao", label: "Data" },
      { chave: "validadeMeses", label: "Validade" },
      { chave: "vencimento", label: "Vencimento" },
      { chave: "avaliador", label: "Avaliador" },
      { chave: "cadastradoPor", label: "Cadastrado por" },
      { chave: "situacao", label: "Situação" },
    ];
  }, [tipoRelatorio, colunasColaboradores]);

  function valorCelula(item, chave) {
    if (chave === "realizado") return item.realizado ? "SIM" : "NÃO";
    if (chave === "detalhesResumo" && Array.isArray(item.detalhesLista)) {
      return item.detalhesLista.join(" | ");
    }
    if (typeof item[chave] === "boolean") return item[chave] ? "SIM" : "NÃO";
    return item[chave] || "-";
  }

  function exportarExcel() {
    if (!resultados.length) {
      alert("Não há dados para exportar.");
      return;
    }

    const linhas = resultados.map((item) => {
      const obj = {};

      colunasTabela.forEach((coluna) => {
        obj[coluna.label] = valorCelula(item, coluna.chave);
      });

      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Relatório");

    XLSX.writeFile(
      workbook,
      `relatorio_${tipoRelatorio}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }

  function exportarPDF() {
    if (!resultados.length) {
      alert("Não há dados para exportar.");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const tituloMap = Object.fromEntries(
      TIPOS_RELATORIO.map((item) => [item.id, item.label]),
    );

    doc.setFontSize(14);
    doc.text(tituloMap[tipoRelatorio] || "Relatório", 14, 14);

    doc.setFontSize(10);
    doc.text(`Gerado em: ${formatarDataBR(new Date())}`, 14, 20);

    if (tipoRelatorio === "colaborador" && dadosCabecalhoColaborador) {
      doc.setFontSize(10);
      doc.text(`Nome: ${dadosCabecalhoColaborador.colaborador}`, 14, 28);
      doc.text(`Obra: ${dadosCabecalhoColaborador.obra}`, 110, 28);
      doc.text(`Função: ${dadosCabecalhoColaborador.funcao}`, 14, 34);
      doc.text(`CPF: ${dadosCabecalhoColaborador.cpf}`, 110, 34);
    }

    autoTable(doc, {
      startY:
        tipoRelatorio === "colaborador" && dadosCabecalhoColaborador ? 40 : 26,
      head: [colunasTabela.map((coluna) => coluna.label)],
      body: resultados.map((item) =>
        colunasTabela.map((coluna) => valorCelula(item, coluna.chave)),
      ),
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [37, 99, 235],
      },
      margin: {
        left: 8,
        right: 8,
      },
    });

    doc.save(
      `relatorio_${tipoRelatorio}_${new Date().toISOString().slice(0, 10)}.pdf`,
    );
  }

  function alternarColunaColaborador(chave) {
    setColunasColaboradores((prev) => {
      if (prev.includes(chave)) {
        return prev.filter((item) => item !== chave);
      }

      return [...prev, chave];
    });
  }

  function irParaTopo() {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  return (
    <div className="relatorios-page">
      <div className="relatorios-container">
        <header className="relatorios-header">
          <div className="relatorios-header-texto">
            <span className="relatorios-badge">Gestão de Treinamentos</span>
            <h1>Relatórios</h1>
            <p>
              Consulte colaboradores, matrizes, lançamentos, vencimentos e
              auditorias do sistema.
            </p>
          </div>
        </header>

        <section className="dashboard-relatorios">
          <div className="dashboard-topo">
            <div>
              <h2>Dashboard</h2>
              <p>
                {tipoRelatorio === "matrizes"
                  ? "Visão geral das matrizes cadastradas e seus treinamentos."
                  : `Lançamentos do ano e do mês atuais${
                      filtroObra !== "Todas" ? ` para ${filtroObra}` : ""
                    }.`}
              </p>
            </div>
          </div>

          <div className="dashboard-grid dashboard-grid-dois">
            <div className="dashboard-card">
              <span>
                {tipoRelatorio === "matrizes"
                  ? "Total de matrizes selecionadas"
                  : "Total de lançamentos no ano atual"}
              </span>
              <strong>
                {loadingDashboard ? "..." : dashboard.totalAnoAtual}
              </strong>
            </div>

            <div className="dashboard-card">
              <span>
                {tipoRelatorio === "matrizes"
                  ? "Total de treinamentos nas matrizes"
                  : "Total de lançamentos no mês atual"}
              </span>
              <strong>
                {loadingDashboard ? "..." : dashboard.totalMesAtual}
              </strong>
            </div>
          </div>
        </section>

        <section className="relatorios-filtros">
          <div className="filtros-topo">
            <h2>Filtros de consulta</h2>
            <p>Selecione o relatório e os filtros desejados.</p>
          </div>

          <div className="filtros-grid">
            <div className="campo">
              <label htmlFor="tipoRelatorio">Tipo de relatório</label>
              <select
                id="tipoRelatorio"
                value={tipoRelatorio}
                onChange={(e) => {
                  const novoTipo = e.target.value;
                  setTipoRelatorio(novoTipo);
                  setBusca("");
                  setResultados([]);
                  setErro("");
                  setFiltroObra("Todas");
                  setFiltroFuncao("Todas");
                  setFiltroTipo("Todos");
                  setFiltroMatriz("Todas");
                  setDiasProximos(30);
                  setDataInicio("");
                  setDataFim("");
                  setFiltroAdministrador("");
                  setFiltroAcaoAuditoria("TODAS");
                  setFiltroEntidadeAuditoria("TODAS");
                  setPeriodoLancamento("mensal");
                  colaboradorSelecionadoRef.current = null;
                  treinamentoSelecionadoRef.current = null;
                  setMostrarSugestoesColaborador(false);
                  setMostrarSugestoesTreinamento(false);
                }}
              >
                {TIPOS_RELATORIO.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            {mostrarBusca && (
              <div
                className="campo campo-busca"
                ref={
                  tipoRelatorio === "colaborador"
                    ? wrapperSugestaoColaboradorRef
                    : tipoRelatorio === "treinamento"
                      ? wrapperSugestaoTreinamentoRef
                      : null
                }
              >
                <label htmlFor="busca">{tituloBusca}</label>
                <input
                  id="busca"
                  type="text"
                  value={busca}
                  onChange={handleBuscaChange}
                  onFocus={() => {
                    if (tipoRelatorio === "colaborador" && busca.trim()) {
                      setMostrarSugestoesColaborador(true);
                    }

                    if (tipoRelatorio === "treinamento" && busca.trim()) {
                      setMostrarSugestoesTreinamento(true);
                    }

                    if (tipoRelatorio === "auditoria" && busca.trim()) {
                      setMostrarSugestoesColaborador(true);
                    }
                  }}
                  placeholder={placeholderBusca}
                  autoComplete="off"
                />

                {(tipoRelatorio === "colaborador" ||
                  tipoRelatorio === "auditoria") &&
                  mostrarSugestoesColaborador &&
                  sugestoesColaboradores.length > 0 && (
                    <div className="sugestoes-box">
                      {sugestoesColaboradores.map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          className="sugestao-item"
                          onMouseDown={() => selecionarColaborador(item)}
                        >
                          <span className="sugestao-nome">{item.nome}</span>
                          <span className="sugestao-info">
                            {item.cpf} • {item.funcao} • {item.obra}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                {tipoRelatorio === "treinamento" &&
                  mostrarSugestoesTreinamento &&
                  sugestoesTreinamentos.length > 0 && (
                    <div className="sugestoes-box">
                      {sugestoesTreinamentos.map((nome) => (
                        <button
                          type="button"
                          key={nome}
                          className="sugestao-item"
                          onMouseDown={() => selecionarTreinamento(nome)}
                        >
                          <span className="sugestao-nome">{nome}</span>
                        </button>
                      ))}
                    </div>
                  )}
              </div>
            )}

            {tipoRelatorio === "matrizes" && (
              <div className="campo">
                <label htmlFor="matriz">Matriz</label>
                <select
                  id="matriz"
                  value={filtroMatriz}
                  onChange={(e) => setFiltroMatriz(e.target.value)}
                  disabled={loadingBase}
                >
                  <option value="Todas">Todas</option>
                  {matrizes.map((matriz) => (
                    <option key={matriz.id} value={matriz.id}>
                      {matriz.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {tipoRelatorio === "auditoria" && (
              <>
                <div className="campo">
                  <label htmlFor="filtroAcaoAuditoria">Ação</label>
                  <select
                    id="filtroAcaoAuditoria"
                    value={filtroAcaoAuditoria}
                    onChange={(e) => setFiltroAcaoAuditoria(e.target.value)}
                  >
                    {OPCOES_ACAO_AUDITORIA.map((item) => (
                      <option key={item.chave} value={item.chave}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="campo">
                  <label htmlFor="filtroEntidadeAuditoria">Entidade</label>
                  <select
                    id="filtroEntidadeAuditoria"
                    value={filtroEntidadeAuditoria}
                    onChange={(e) => setFiltroEntidadeAuditoria(e.target.value)}
                  >
                    {OPCOES_ENTIDADE_AUDITORIA.map((item) => (
                      <option key={item.chave} value={item.chave}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {mostrarFiltroObra && (
              <div className="campo">
                <label htmlFor="obra">Obra</label>
                <select
                  id="obra"
                  value={filtroObra}
                  onChange={(e) => setFiltroObra(e.target.value)}
                  disabled={loadingBase}
                >
                  <option value="Todas">Todas</option>
                  {obras.map((obra) => (
                    <option key={obra} value={obra}>
                      {obra}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {mostrarFiltroFuncao && (
              <div className="campo">
                <label htmlFor="funcao">Função</label>
                <select
                  id="funcao"
                  value={filtroFuncao}
                  onChange={(e) => setFiltroFuncao(e.target.value)}
                  disabled={loadingBase}
                >
                  <option value="Todas">Todas</option>
                  {funcoes.map((funcao) => (
                    <option key={funcao} value={funcao}>
                      {funcao}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {mostrarFiltroTipo && (
              <div className="campo">
                <label htmlFor="tipoTreinamento">Tipo</label>
                <select
                  id="tipoTreinamento"
                  value={filtroTipo}
                  onChange={(e) => setFiltroTipo(e.target.value)}
                >
                  <option value="Todos">Todos</option>
                  <option value="OBRIGATORIO">OBRIGATÓRIO</option>
                  <option value="EVENTUAL">EVENTUAL</option>
                </select>
              </div>
            )}

            {mostrarFiltroDias && (
              <div className="campo">
                <label htmlFor="diasProximos">Dias para aviso</label>
                <input
                  id="diasProximos"
                  type="number"
                  min="1"
                  max="365"
                  value={diasProximos}
                  onChange={(e) => setDiasProximos(e.target.value)}
                  placeholder="30"
                />
              </div>
            )}

            {mostrarFiltroPeriodo && (
              <>
                <div className="campo">
                  <label htmlFor="dataInicio">Data inicial</label>
                  <input
                    id="dataInicio"
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                  />
                </div>

                <div className="campo">
                  <label htmlFor="dataFim">Data final</label>
                  <input
                    id="dataFim"
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                  />
                </div>
              </>
            )}

            {mostrarFiltroAdministrador && (
              <div className="campo">
                <label htmlFor="filtroAdministrador">Administrador</label>
                <input
                  id="filtroAdministrador"
                  type="text"
                  value={filtroAdministrador}
                  onChange={(e) =>
                    setFiltroAdministrador(textoMaiusculo(e.target.value))
                  }
                  placeholder="NOME DO ADMINISTRADOR"
                />
              </div>
            )}

            {mostrarFiltroLancamento && (
              <div className="campo">
                <label htmlFor="periodoLancamento">Agrupar por</label>
                <select
                  id="periodoLancamento"
                  value={periodoLancamento}
                  onChange={(e) => setPeriodoLancamento(e.target.value)}
                >
                  {OPCOES_LANCAMENTOS.map((item) => (
                    <option key={item.chave} value={item.chave}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {mostrarColunasColaboradores && (
            <div className="opcoes-colunas-relatorio">
              <h3>Escolha o que sairá no relatório</h3>

              <div>
                {OPCOES_COLABORADORES.map((item) => (
                  <label key={item.chave}>
                    <input
                      type="checkbox"
                      checked={colunasColaboradores.includes(item.chave)}
                      onChange={() => alternarColunaColaborador(item.chave)}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="filtros-acoes">
            <button className="botao-secundario" onClick={limparFiltros}>
              Limpar
            </button>

            <button
              className="botao-primario"
              onClick={gerarRelatorio}
              disabled={loadingBase || loadingRelatorio}
            >
              {loadingRelatorio ? "Gerando..." : "Gerar relatório"}
            </button>
          </div>

          {erro && <div className="mensagem-erro">{erro}</div>}
        </section>

        <section
          className={`relatorios-resumo ${
            tipoRelatorio === "matrizes" ? "relatorios-resumo-matrizes" : ""
          }`}
        >
          {tipoRelatorio === "matrizes" ? (
            <>
              <div className="resumo-card">
                <span>Matrizes no resultado</span>
                <strong>{resumo.matrizes}</strong>
              </div>

              <div className="resumo-card">
                <span>Treinamentos</span>
                <strong>{resumo.total}</strong>
              </div>

              <div className="resumo-card">
                <span>Obrigatórios</span>
                <strong>{resumo.obrigatorios}</strong>
              </div>

              <div className="resumo-card">
                <span>Eventuais</span>
                <strong>{resumo.eventuais}</strong>
              </div>

              <div className="resumo-card">
                <span>Com validade</span>
                <strong>{resumo.comValidade}</strong>
              </div>

              <div className="resumo-card">
                <span>Sem validade</span>
                <strong>{resumo.semValidade}</strong>
              </div>
            </>
          ) : (
            <>
              <div className="resumo-card">
                <span>Total de registros</span>
                <strong>{resumo.total}</strong>
              </div>

              <div className="resumo-card">
                <span>Realizados</span>
                <strong>{resumo.realizados}</strong>
              </div>

              <div className="resumo-card">
                <span>Pendentes</span>
                <strong>{resumo.pendentes}</strong>
              </div>

              <div className="resumo-card">
                <span>Vencidos</span>
                <strong>{resumo.vencidos}</strong>
              </div>

              <div className="resumo-card">
                <span>Próximos</span>
                <strong>{resumo.proximos}</strong>
              </div>

              <div className="resumo-card">
                <span>Em dia</span>
                <strong>{resumo.emDia}</strong>
              </div>
            </>
          )}
        </section>

        {tipoRelatorio === "colaborador" && dadosCabecalhoColaborador && (
          <section className="bloco-colaborador">
            <div className="colaborador-topo-grid">
              <div className="colaborador-card grande">
                <span>NOME DO COLABORADOR</span>
                <strong>{dadosCabecalhoColaborador.colaborador}</strong>
              </div>

              <div className="colaborador-card media">
                <span>OBRA</span>
                <strong>{dadosCabecalhoColaborador.obra}</strong>
              </div>

              <div className="colaborador-card media">
                <span>FUNÇÃO</span>
                <strong>{dadosCabecalhoColaborador.funcao}</strong>
              </div>

              <div className="colaborador-card media">
                <span>CPF</span>
                <strong>{dadosCabecalhoColaborador.cpf}</strong>
              </div>
            </div>
          </section>
        )}

        <section className="relatorios-resultado">
          <div className="resultado-topo">
            <div>
              <h2>Resultado da consulta</h2>
              <p>{resultados.length} registro(s) encontrado(s)</p>
            </div>

            <div className="resultado-acoes">
              <button
                className="botao-exportar"
                onClick={exportarPDF}
                disabled={!resultados.length}
              >
                Baixar PDF
              </button>

              <button
                className="botao-exportar"
                onClick={exportarExcel}
                disabled={!resultados.length}
              >
                Baixar Excel
              </button>
            </div>
          </div>

          <div className="tabela-wrapper">
            <table
              className={`tabela-relatorios ${
                tipoRelatorio === "matrizes" ? "tabela-matrizes" : ""
              }`}
            >
              <thead>
                <tr>
                  {colunasTabela.map((coluna) => (
                    <th key={coluna.chave}>{coluna.label}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {resultados.length > 0 ? (
                  resultados.map((item) => (
                    <tr key={item.id}>
                      {colunasTabela.map((coluna) => (
                        <td key={`${item.id}-${coluna.chave}`}>
                          {coluna.chave === "detalhesResumo" ? (
                            <div className="detalhes-auditoria-lista">
                              {(
                                item.detalhesLista || [
                                  valorCelula(item, coluna.chave),
                                ]
                              ).map((detalhe, index) => (
                                <p key={`${item.id}-detalhe-${index}`}>
                                  {detalhe}
                                </p>
                              ))}
                            </div>
                          ) : coluna.chave === "tipoTreinamento" ? (
                            <span
                              className={`tipo-badge ${
                                normalizarTexto(item.tipoTreinamento) ===
                                "OBRIGATORIO"
                                  ? "tipo-obrigatorio"
                                  : "tipo-eventual"
                              }`}
                            >
                              {valorCelula(item, coluna.chave)}
                            </span>
                          ) : coluna.chave === "realizado" ? (
                            <span
                              className={`realizado-badge ${
                                item.realizado
                                  ? "realizado-sim"
                                  : "realizado-nao"
                              }`}
                            >
                              {item.realizado ? "Sim" : "Não"}
                            </span>
                          ) : coluna.chave === "situacao" ? (
                            <span
                              className={`status-badge ${
                                item.situacao === "Em dia"
                                  ? "status-em-dia"
                                  : item.situacao === "Vencido"
                                    ? "status-vencido"
                                    : item.situacao === "Próximo do vencimento"
                                      ? "status-proximo"
                                      : item.situacao === "Pendente"
                                        ? "status-pendente"
                                        : "status-sem-validade"
                              }`}
                            >
                              {item.situacao}
                            </span>
                          ) : coluna.chave === "matrizAtiva" ||
                            coluna.chave === "treinamentoAtivo" ? (
                            <span
                              className={`status-badge ${
                                valorCelula(item, coluna.chave) === "ATIVO" ||
                                valorCelula(item, coluna.chave) === "ATIVA"
                                  ? "status-em-dia"
                                  : "status-sem-validade"
                              }`}
                            >
                              {valorCelula(item, coluna.chave)}
                            </span>
                          ) : (
                            valorCelula(item, coluna.chave)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={colunasTabela.length}
                      className="sem-resultados"
                    >
                      {loadingRelatorio
                        ? "Gerando relatório..."
                        : "Nenhum resultado encontrado."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="botoes-finais-relatorios">
          <button type="button" onClick={irParaTopo}>
            SUBIR AO TOPO
          </button>

          <button type="button" onClick={() => navigate("/main")}>
            VOLTAR
          </button>
        </div>
      </div>
    </div>
  );
};

export default Relatorios;
